import { features } from "@/config";

export enum LabTestGroup {
  ClassificationAndIndex = "Classification and Index Testing",
  UndrainedShearStrengthIndex = "Undrained Shear Strength Index Tests",
  DirectShear = "Direct Shear Testing",
}

export interface LabTestMethod {
  code: string;
  name: string;
  conventionalName: string;
  title: string;
  standard: string;
  standardGroup: "ISO" | "ASTM" | "NEN" | "NCh" | "BS";
  /** Level-2 "Test Name" label (filter tree, Add Test modal); defaults to the type's `name`. */
  testName?: string;
  /** Level-3 "Standard" label (filter tree, Add Test modal); defaults to `standard`. */
  standardLabel?: string;
  importHeader?: string;
  canCreate: boolean;
  hide?: boolean;
}

export interface LabTestType {
  code: string;
  group: LabTestGroup;
  name: string;
  types: LabTestMethod[];
  hide?: boolean;
  /** Report covers the project's tests of this type; the preview renders the full reporting set. */
  multiTestReport?: boolean;
}

interface LabTestTypeDefinition {
  group: LabTestGroup;
  name: string;
  methods: Record<string, LabTestMethod>;
  hide?: boolean;
  multiTestReport?: boolean;
}

/** Single registration point for test types: codes, display names, and metadata in one entry. */
const definitions = {
  DSS: {
    group: LabTestGroup.DirectShear,
    name: "Direct Simple Shear",
    methods: {
      NEN: {
        code: "DSS_NEN",
        name: "Direct Simple Shear - ASTM D6528-24 + NEN-8992:2025",
        conventionalName: "Determination of shear strength by direct simple shear",
        title: "DSS (Direct Simple Shear - ASTM D6528-24 + NEN-8992:2025)",
        standard: "ASTM D6528-24 + NEN-8992:2025",
        standardGroup: "NEN",
        importHeader: "DSS NEN",
        canCreate: true,
        hide: !features.previewFeatures,
      },
    },
    hide: !features.previewFeatures,
  },
  PSD: {
    group: LabTestGroup.ClassificationAndIndex,
    name: "Particle-Size Distribution",
    methods: {
      ISO_WS: {
        code: "PSD_ISO_WS",
        name: "Sieve - ISO 17892-4:2016",
        conventionalName: "Determination of particle size distribution",
        title: "PSD (Sieve - ISO 17892-4:2016)",
        standard: "ISO 17892-4:2016",
        standardGroup: "ISO",
        testName: "PSD Sieve",
        importHeader: "Sieve ISO",
        canCreate: true,
      },
      ISO_HY: {
        code: "PSD_ISO_HY",
        name: "Hydrometer - ISO 17892-4:2016",
        conventionalName: "Determination of particle size distribution",
        title: "PSD (Hydrometer - ISO 17892-4:2016)",
        standard: "ISO 17892-4:2016",
        standardGroup: "ISO",
        testName: "PSD Hydrometer",
        importHeader: "Hydrometer ISO",
        canCreate: true,
      },
      ISO_PP: {
        code: "PSD_ISO_PP",
        name: "Pipette - ISO 17892-4:2016",
        conventionalName: "Determination of particle size distribution",
        title: "PSD (Pipette - ISO 17892-4:2016)",
        standard: "ISO 17892-4:2016",
        standardGroup: "ISO",
        testName: "PSD Pipette",
        importHeader: "Pipette ISO",
        canCreate: false,
        hide: true,
      },
      ISO_WS_HY: {
        code: "PSD_ISO_WS_HY",
        name: "Sieve + Hydrometer - ISO 17892-4:2016",
        conventionalName: "Determination of particle size distribution",
        title: "PSD (Sieve + Hydrometer - ISO 17892-4:2016)",
        standard: "ISO 17892-4:2016",
        standardGroup: "ISO",
        testName: "PSD Sieve and Hydrometer",
        canCreate: true,
      },
      ISO_WS_PP: {
        code: "PSD_ISO_WS_PP",
        name: "Sieve + Pipette - ISO 17892-4:2016",
        conventionalName: "Determination of particle size distribution",
        title: "PSD (Sieve + Pipette - ISO 17892-4:2016)",
        standard: "ISO 17892-4:2016",
        standardGroup: "ISO",
        testName: "PSD Sieve and Pipette",
        canCreate: false,
        hide: true,
      },
      ASTM_SY: {
        code: "PSD_ASTM_SY",
        name: "Sieve - ASTM D6913/D6913M - 17",
        conventionalName: "Particle-Size Distribution (Gradation) of Soils Using Sieve Analysis",
        title: "PSD (Sieve - ASTM D6913/D6913M - 17)",
        standard: "ASTM D6913/D6913M - 17",
        standardGroup: "ASTM",
        testName: "PSD Sieve",
        importHeader: "Sieve ASTM",
        canCreate: true,
      },
      ASTM_HY: {
        code: "PSD_ASTM_HY",
        name: "Hydrometer - ASTM D7928 - 21",
        conventionalName: "Particle-Size Distribution (Gradation) of fine grained soils using hydrometer analysis",
        title: "PSD (Hydrometer - ASTM D7928 - 21)",
        standard: "ASTM D7928 - 21",
        standardGroup: "ASTM",
        testName: "PSD Hydrometer",
        importHeader: "Hydrometer ASTM",
        canCreate: true,
      },
      ASTM_SY_HY: {
        code: "PSD_ASTM_SY_HY",
        name: "Sieve + Hydrometer - ASTM D6913/D6913M - 17 + D7928 - 21",
        conventionalName: "Particle-Size Distribution (Gradation) of soils using sieve and hydrometer analysis",
        title: "PSD (Sieve + Hydrometer - ASTM D6913/D6913M - 17 + D7928 - 21)",
        standard: "ASTM D6913/D6913M - 17 + D7928 - 21",
        standardGroup: "ASTM",
        testName: "PSD Sieve and Hydrometer",
        canCreate: true,
      },
    },
  },
  PI: {
    group: LabTestGroup.ClassificationAndIndex,
    name: "Atterberg Limits / Plasticity Index",
    multiTestReport: true,
    methods: {
      ISO_FC: {
        code: "PI_ISO_FC",
        name: "Atterberg Limits / Plasticity Index - ISO 17892-12:2018 - Fallcone",
        conventionalName: "Determination of liquid and plastic limits",
        title: "PI (Atterberg Limits / Plasticity Index - ISO 17892-12:2018 - Fallcone)",
        standard: "ISO 17892-12:2018",
        standardGroup: "ISO",
        standardLabel: "ISO 17892-12:2018 (Fall Cone)",
        importHeader: "PI ISO FC",
        canCreate: true,
      },
      ASTM_CC: {
        code: "PI_ASTM_CC",
        name: "Atterberg Limits / Plasticity Index - ASTM D4318 - 17 - Casagrande cup",
        conventionalName: "Determination of liquid limit, plastic limit, and plasticity index",
        title: "PI (Atterberg Limits / Plasticity Index - ASTM D4318 - 17 - Casagrande cup)",
        standard: "ASTM D4318 - 17",
        standardGroup: "ASTM",
        standardLabel: "ASTM D4318 - 17 (Casagrande)",
        importHeader: "PI ASTM CC",
        canCreate: true,
      },
    },
  },
  WC: {
    group: LabTestGroup.ClassificationAndIndex,
    name: "Water Content / Moisture Content",
    multiTestReport: true,
    methods: {
      ISO: {
        code: "WC_ISO",
        name: "Water Content - ISO 17892-1",
        conventionalName: "Determination of water content",
        title: "WC (Water Content - ISO 17892-1)",
        standard: "ISO 17892-1:2016",
        standardGroup: "ISO",
        importHeader: "WC ISO",
        canCreate: true,
      },
      ASTM: {
        code: "WC_ASTM",
        name: "Water Content - ASTM D2216",
        conventionalName: "Determination of water content",
        title: "WC (Water Content - ASTM D2216)",
        standard: "ASTM D2216 - 19",
        standardGroup: "ASTM",
        importHeader: "WC ASTM",
        canCreate: true,
      },
      NCH: {
        code: "WC_NCH",
        name: "Water Content - NCh1515Of.79",
        conventionalName: "Determination of water content",
        title: "WC (Water Content - NCh1515Of.79)",
        standard: "NCh1515Of.79",
        standardGroup: "NCh",
        importHeader: "WC NCh",
        canCreate: true,
      },
    },
  },
  BD: {
    group: LabTestGroup.ClassificationAndIndex,
    name: "Density / Unit Weight",
    multiTestReport: true,
    methods: {
      ISO: {
        code: "BD_ISO",
        name: "Density / Unit Weight - ISO 17892-2:2014",
        conventionalName: "Determination of bulk density",
        title: "BD (Density / Unit Weight - ISO 17892-2:2014)",
        standard: "ISO 17892-2:2014",
        standardGroup: "ISO",
        importHeader: "BD ISO",
        canCreate: true,
      },
      ASTM: {
        code: "BD_ASTM",
        name: "Density / Unit Weight - ASTM D7263",
        conventionalName: "Determination of bulk density of soil specimens",
        title: "BD (Density / Unit Weight - ASTM D7263)",
        standard: "ASTM D7263",
        standardGroup: "ASTM",
        importHeader: "BD ASTM",
        canCreate: true,
      },
    },
  },
  FC: {
    group: LabTestGroup.UndrainedShearStrengthIndex,
    name: "Fall Cone",
    multiTestReport: true,
    methods: {
      ISO: {
        code: "FC_ISO",
        name: "Fall Cone - ISO 17892-6:2017",
        conventionalName: "Determination of undrained shear strength and sensitivity by fall cone",
        title: "FC (Fall Cone - ISO 17892-6:2017)",
        standard: "ISO 17892-6:2017",
        standardGroup: "ISO",
        importHeader: "FC ISO",
        canCreate: true,
      },
    },
  },
  PD: {
    group: LabTestGroup.ClassificationAndIndex,
    name: "Particle Density / Specific Gravity",
    multiTestReport: true,
    methods: {
      ASTM: {
        code: "PD_ASTM_FL",
        name: "Fluid Pycnometer - ASTM D854-23",
        conventionalName: "Determination of specific gravity of soil solids",
        title: "PD (Fluid Pycnometer - ASTM D854-23)",
        standard: "ASTM D854-23",
        standardGroup: "ASTM",
        standardLabel: "ASTM D854-23 (Water)",
        importHeader: "SG ASTM",
        canCreate: true,
      },
      ISO: {
        code: "PD_ISO_FL",
        name: "Fluid Pycnometer - ISO 17892-3:2015",
        conventionalName: "Determination of particle density",
        title: "PD (Fluid Pycnometer - ISO 17892-3:2015)",
        standard: "ISO 17892-3:2015",
        standardGroup: "ISO",
        standardLabel: "ISO 17892-3:2015 (Fluid)",
        importHeader: "PD ISO",
        canCreate: true,
      },
      NCH: {
        code: "PD_NCH_FL",
        name: "Fluid Pycnometer - NCh1532Of.80",
        conventionalName: "Determination of particle density",
        title: "PD (Fluid Pycnometer - NCh1532Of.80)",
        standard: "NCh1532Of.80",
        standardGroup: "NCh",
        standardLabel: "NCh1532Of.80 (Fluid)",
        canCreate: true,
      },
    },
  },
  PP: {
    group: LabTestGroup.UndrainedShearStrengthIndex,
    name: "Pocket Penetrometer",
    multiTestReport: true,
    methods: {
      ISO: {
        code: "PP_ISO",
        name: "Pocket Penetrometer - ISO 19901-8:2023",
        conventionalName: "Pocket Penetrometer",
        title: "PP (Pocket Penetrometer - ISO 19901-8:2023)",
        standard: "ISO 19901-8:2023",
        standardGroup: "ISO",
        importHeader: "PP ISO",
        canCreate: true,
      },
    },
  },
  TV: {
    group: LabTestGroup.UndrainedShearStrengthIndex,
    name: "Torvane",
    multiTestReport: true,
    methods: {
      ISO: {
        code: "TV_ISO",
        name: "Torvane - ISO 19901-8:2023",
        conventionalName: "TorVane",
        title: "TV (Torvane - ISO 19901-8:2023)",
        standard: "ISO 19901-8:2023",
        standardGroup: "ISO",
        importHeader: "TV ISO",
        canCreate: true,
      },
    },
  },
  MV: {
    group: LabTestGroup.UndrainedShearStrengthIndex,
    name: "Miniature Vane",
    multiTestReport: true,
    methods: {
      ASTM: {
        code: "MV_ASTM",
        name: "Miniature Vane - ASTM D4648 / D4648M-24",
        conventionalName: "Miniature Vane",
        title: "MV (Miniature Vane - ASTM D4648 / D4648M-24)",
        standard: "ASTM D4648 / D4648M-24",
        standardGroup: "ASTM",
        importHeader: "MV ASTM",
        canCreate: true,
      },
    },
  },
  MM: {
    group: LabTestGroup.ClassificationAndIndex,
    name: "Minimum/Maximum Index Density",
    multiTestReport: true,
    methods: {
      ASTM: {
        code: "MM_ASTM",
        name: "Minimum/Maximum Index Density - ASTM D4253-16 + D4254-16",
        conventionalName: "Maximum and minimum index densities and unit weights of soils",
        title: "MM (Minimum/Maximum Index Density - ASTM D4253-16 + D4254-16)",
        standard: "ASTM D4253-16 + D4254-16",
        standardGroup: "ASTM",
        importHeader: "MM ASTM",
        canCreate: true,
      },
    },
  },
  PF: {
    group: LabTestGroup.ClassificationAndIndex,
    name: "Percentage Fines",
    multiTestReport: true,
    methods: {
      ASTM: {
        code: "PF_ASTM",
        name: "Percentage Fines - ASTM D1140-25",
        conventionalName: "Determination of the amount of material finer than the 75 µm (No. 200) sieve",
        title: "PF (Percentage Fines - ASTM D1140-25)",
        standard: "ASTM D1140-25",
        standardGroup: "ASTM",
        importHeader: "PF ASTM",
        canCreate: true,
      },
    },
  },
  CC: {
    group: LabTestGroup.ClassificationAndIndex,
    name: "Calcium Carbonate Content",
    hide: !features.previewFeatures,
    multiTestReport: true,
    methods: {
      ASTM: {
        code: "CC_ASTM",
        name: "Calcium Carbonate Content - ASTM D4373 - 21",
        conventionalName: "Rapid determination of carbonate content of soils (calcite equivalent)",
        title: "CC (Calcium Carbonate Content - ASTM D4373 - 21)",
        standard: "ASTM D4373 - 21",
        standardGroup: "ASTM",
        importHeader: "CC ASTM",
        canCreate: true,
        hide: !features.previewFeatures,
      },
    },
  },
  LS: {
    group: LabTestGroup.ClassificationAndIndex,
    name: "Linear Shrinkage",
    hide: !features.previewFeatures,
    methods: {
      BS: {
        code: "LS_BS",
        name: "Linear Shrinkage - BS 1377-2:2022 cl.7.0",
        conventionalName: "Determination of the linear shrinkage of a soil bar",
        title: "LS (Linear Shrinkage - BS 1377-2:2022 cl.7.0)",
        standard: "BS 1377-2:2022 cl.7.0",
        standardGroup: "BS",
        importHeader: "LS BS",
        canCreate: true,
        hide: !features.previewFeatures,
      },
    },
  },
  SPV: {
    group: LabTestGroup.ClassificationAndIndex,
    name: "Soil pH Value",
    hide: !features.previewFeatures,
    methods: {
      ASTM: {
        code: "SPV_ASTM",
        name: "Soil pH Value - ASTM D4972-19(2024) (Method A)",
        conventionalName: "Determination of the pH of soils",
        title: "SPV (Soil pH Value - ASTM D4972-19(2024) (Method A))",
        standard: "ASTM D4972-19(2024)",
        standardGroup: "ASTM",
        importHeader: "SPV ASTM",
        canCreate: true,
        hide: !features.previewFeatures,
      },
    },
  },
} as const satisfies Record<string, LabTestTypeDefinition>;

type Definitions = typeof definitions;

// Object.fromEntries widens keys/values to string; the casts restore the literal
// types carried by `definitions` so `TestMethod.WC.ISO` narrows to "WC_ISO".
export const TestType = Object.fromEntries(Object.keys(definitions).map((code) => [code, code])) as {
  [K in keyof Definitions]: K;
};

type MethodCodes<TMethods extends Record<string, { code: string }>> = {
  [M in keyof TMethods]: TMethods[M]["code"];
};

export const TestMethod = Object.fromEntries(
  Object.entries(definitions).map(([typeCode, definition]) => [
    typeCode,
    Object.fromEntries(Object.entries(definition.methods).map(([alias, method]) => [alias, method.code])),
  ]),
) as {
  [K in keyof Definitions]: MethodCodes<Definitions[K]["methods"]>;
};

export type TestTypeValue = (typeof TestType)[keyof typeof TestType];

export const LabTestTypes: LabTestType[] = Object.entries<LabTestTypeDefinition>(definitions).map(
  ([code, definition]) => ({
    code,
    group: definition.group,
    name: definition.name,
    types: Object.values(definition.methods),
    hide: definition.hide,
    multiTestReport: definition.multiTestReport,
  }),
);

export const findTestType = (code: string): LabTestType | undefined => {
  return LabTestTypes.find((test) => test.code === code);
};

export const getMethodCodesForTestType = (typeCode: string): string[] =>
  findTestType(typeCode)?.types.map((method) => method.code) ?? [];

export const isMultiTestReportType = (type: string): boolean => findTestType(type)?.multiTestReport ?? false;

export const findTestMethod = (typeCode: string, methodCode: string): LabTestMethod | undefined => {
  return LabTestTypes.find((test) => test.code === typeCode)?.types.find((type) => type.code === methodCode);
};

export const methodSlug = (methodCode: string): string => methodCode.split("_").slice(1).join("-").toLowerCase();

export const findTestMethodBySlug = (typeCode: string, slug: string): LabTestMethod | undefined =>
  findTestType(typeCode)?.types.find((method) => methodSlug(method.code) === slug);

// Imperial units are an ASTM-only affordance, so the method's own `standardGroup` is the whole gate —
// derived here rather than listed anywhere, which is what stops a new ASTM method from being switchable
// in one place and not another. An unknown method does not switch.
export const isUnitSwitchable = (typeCode: string, methodCode: string): boolean =>
  findTestMethod(typeCode, methodCode)?.standardGroup === "ASTM";

export const getTestTitle = (typeCode: string, methodCode: string): string =>
  findTestMethod(typeCode, methodCode)?.title ?? methodCode;

export const getTestDetailsTitle = (typeCode: string, methodCode: string, sampleId?: string | null): string =>
  `${getTestTitle(typeCode, methodCode)}${sampleId ? ` ${sampleId}` : ""}`;

export const combinedMethodParts: Record<string, [string, string]> = {
  [TestMethod.PSD.ISO_WS_HY]: [TestMethod.PSD.ISO_WS, TestMethod.PSD.ISO_HY],
  [TestMethod.PSD.ISO_WS_PP]: [TestMethod.PSD.ISO_WS, TestMethod.PSD.ISO_PP],
  [TestMethod.PSD.ASTM_SY_HY]: [TestMethod.PSD.ASTM_SY, TestMethod.PSD.ASTM_HY],
};
